import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tag, Search, Copy, Facebook, Linkedin, MessageCircle, Users2, RefreshCw, MapPin, ExternalLink, BadgePercent } from "lucide-react";
import { toast } from "sonner";
import FbGroupShare from "@/components/FbGroupShare";
import { SocialPublishBadge } from "@/components/SocialPublishBadge";

const API = process.env.REACT_APP_BACKEND_URL;

function OfferSharePanel({ o, onTracked }) {
  const [showGroups, setShowGroups] = useState(false);
  const biz = o.business || {};
  const city = o.city || biz.city || "";
  const shareUrl = `${API}/api/offre-vitrine/${o.id}`;

  const lines = [`🏷️ ${o.title}`];
  if (o.discount) lines.push(`💥 ${o.discount}`);
  const loc = [biz.name, city].filter(Boolean).join(" — ");
  if (loc) lines.push(`📍 ${loc}`);
  if (o.conditions) lines.push(`ℹ️ ${o.conditions}`);
  if (o.promo_code) lines.push(`🔖 Code : ${o.promo_code}`);
  if (o.description) lines.push(`\n${o.description.trim()}`);
  lines.push(`\n👉 J'en profite : ${shareUrl}`);
  const text = lines.join("\n");

  const track = async (net) => {
    try { const { data } = await api.post("/admin/social/track", { kind: "offer", id: o.id, network: net }); onTracked?.(o.id, data.social_posts, data.social_counts); }
    catch (e) { void e; }
  };

  const copy = async () => { try { await navigator.clipboard.writeText(text); toast.success("Texte + lien copiés — prêts à coller !"); } catch { toast.message("Copie manuelle requise."); } };
  const openFb = () => { try { navigator.clipboard.writeText(text); } catch (e) { void e; } track("facebook"); window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, "_blank", "noopener"); };
  const openLinkedin = () => { try { navigator.clipboard.writeText(text); } catch (e) { void e; } track("linkedin"); toast.success("Texte copié — collez-le dans LinkedIn."); window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, "_blank", "noopener"); };
  const openWa = () => { track("whatsapp"); window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener"); };

  return (
    <div className="border-t border-slate-200 bg-slate-50 p-4" data-testid={`offer-share-panel-${o.id}`}>
      <div className="flex flex-wrap gap-2 items-center">
        <Button onClick={copy} className="btn-shine text-white" data-testid={`offer-copy-${o.id}`}><Copy size={14} className="mr-1.5" /> Copier texte + lien</Button>
        <button onClick={openFb} data-testid={`offer-fb-${o.id}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#1877F2] text-white text-xs font-semibold hover:opacity-90"><Facebook size={14} /> Facebook</button>
        <button onClick={() => setShowGroups((v) => !v)} data-testid={`offer-groups-${o.id}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-800 text-white text-xs font-semibold hover:opacity-90"><Users2 size={14} /> Groupes</button>
        <button onClick={openLinkedin} data-testid={`offer-linkedin-${o.id}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#0A66C2] text-white text-xs font-semibold hover:opacity-90"><Linkedin size={14} /> LinkedIn</button>
        <button onClick={openWa} data-testid={`offer-whatsapp-${o.id}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-600 text-white text-xs font-semibold hover:opacity-90"><MessageCircle size={14} /> WhatsApp</button>
        <a href={`/offre/${o.id}`} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#E05638]"><ExternalLink size={13} /> Voir la page</a>
      </div>
      {showGroups && <FbGroupShare text={text} shareUrl={shareUrl} cityHint={city} onShared={() => track("groupes")} />}
    </div>
  );
}

export default function AdminOfferShare() {
  const [offers, setOffers] = useState([]);
  const [cats, setCats] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [cat, setCat] = useState("");
  const [pub, setPub] = useState("");
  const [openId, setOpenId] = useState("");

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data || [])).catch(() => {});
    api.get("/cities").then((r) => setCities(r.data || [])).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    const p = new URLSearchParams();
    p.set("status", "published");
    if (city) p.set("city", city);
    if (cat) p.set("category", cat);
    try { const { data } = await api.get(`/admin/offers?${p.toString()}`); setOffers(Array.isArray(data) ? data : (data.items || [])); }
    catch { toast.error("Erreur de chargement."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [city, cat]);

  const onTracked = (id, social_posts, social_counts) => {
    setOffers((list) => list.map((o) => (o.id === id ? { ...o, social_posts, social_counts } : o)));
  };

  const isPublished = (o) => Object.keys(o.social_posts || {}).length > 0;

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return offers.filter((o) => {
      if (pub === "yes" && !isPublished(o)) return false;
      if (pub === "no" && isPublished(o)) return false;
      if (t && !`${o.title} ${o.city || ""} ${o.business?.name || ""} ${o.discount || ""}`.toLowerCase().includes(t)) return false;
      return true;
    });
  }, [offers, q, pub]);

  return (
    <div data-testid="admin-offer-share">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2" data-testid="admin-offer-share-title">
            <Tag className="text-[#E05638]" size={26} /> Publications des offres
          </h1>
          <p className="text-slate-500 text-xs md:text-sm mt-1">Choisissez n'importe quelle offre validée (publiée) puis partagez-la (Facebook, groupes, WhatsApp, LinkedIn) — même principe que les publications des pros.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="admin-offer-share-reload">
          <RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`} /> Actualiser
        </Button>
      </div>

      {/* Filtres identiques à la page « Bons plans » + statut réseaux */}
      <div className="mt-5 grid md:grid-cols-4 gap-3">
        <Select value={city} onValueChange={(v) => setCity(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="offers-city"><SelectValue placeholder="Toutes les villes" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les villes</SelectItem>
            {cities.map((c) => (<SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={cat} onValueChange={(v) => setCat(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="offers-cat"><SelectValue placeholder="Toutes catégories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes catégories</SelectItem>
            {cats.map((c) => (<SelectItem key={c.id} value={c.slug}>{c.name}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={pub} onValueChange={(v) => setPub(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="offers-pub"><SelectValue placeholder="Statut réseaux" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes (publiées ou non)</SelectItem>
            <SelectItem value="yes">Déjà publiées</SelectItem>
            <SelectItem value="no">Non publiées</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (titre, commerce…)" className="pl-9" data-testid="admin-offer-share-search" />
        </div>
      </div>
      <div className="mt-2 text-xs text-slate-500" data-testid="offers-count">{loading ? "Chargement…" : `${filtered.length} offre${filtered.length > 1 ? "s" : ""}`}</div>

      {loading ? (
        <div className="text-slate-500 text-sm mt-6">Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="mt-6 text-slate-500 text-sm bg-white border border-dashed rounded-2xl p-8 text-center">Aucune offre ne correspond à vos critères.</div>
      ) : (
        <div className="mt-4 space-y-3" data-testid="admin-offer-share-list">
          {filtered.map((o) => {
            const img = o.image || (o.images && o.images[0]) || "";
            const biz = o.business || {};
            return (
              <div key={o.id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid={`offer-item-${o.id}`}>
                <button onClick={() => setOpenId((v) => (v === o.id ? "" : o.id))} className="w-full p-3 flex items-center gap-3 text-left hover:bg-slate-50" data-testid={`offer-toggle-${o.id}`}>
                  {img
                    ? <img src={img} alt="" className="w-16 h-16 rounded-lg object-cover bg-slate-100 shrink-0" />
                    : <div className="w-16 h-16 rounded-lg bg-slate-100 grid place-items-center text-slate-300 shrink-0"><BadgePercent size={22} /></div>}
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 truncate">{o.title}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5 flex-wrap">
                      {o.discount && <span className="px-1.5 py-0.5 rounded bg-orange-100 text-[#E05638] text-[10px] font-bold">{o.discount}</span>}
                      {biz.name && <span>{biz.name}</span>}
                      {(o.city || biz.city) && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {o.city || biz.city}</span>}
                    </div>
                    <div className="mt-1.5"><SocialPublishBadge socialPosts={o.social_posts} socialCounts={o.social_counts} testid={`offer-pub-${o.id}`} /></div>
                  </div>
                  <span className="text-xs font-semibold text-[#E05638] shrink-0">{openId === o.id ? "Fermer" : "Partager"}</span>
                </button>
                {openId === o.id && <OfferSharePanel o={o} onTracked={onTracked} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
